import type { Metadata } from "next";
import { axiosWithoutToken } from "@/utils/axios";
import { Property } from "@/types/property";
import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
import {
  buildLocationPath,
  buildPropertyDetailPath,
  propertyStateSlug,
} from "@/lib/seo/property-urls";
import {
  absoluteMediaUrl,
  firstImage,
  firstVideo,
  isVideoFile,
  videoPosterUrl,
} from "@/lib/media";
import {
  isLandProperty,
  landSizeLabel,
  roomCounts,
  roomSummary,
} from "@/lib/property-display";

export const SITE_URL =
  process.env.NEXT_PUBLIC_BASE_URL || "https://www.findnigeriaproperties.com";

/** 1200x630 branded default, used when a listing has no usable photo. */
export const DEFAULT_OG_IMAGE_PATH = "/og/fnp-default.jpg";

export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;

// Re-exported so existing call sites keep working; the implementation lives in
// `lib/media.ts` alongside the rest of the media helpers.
export { isVideoFile };

/**
 * Absolute URL for a stored media path. The fallback is a purpose-built
 * 1200x630 asset rather than `logo.png`: a 74x72 logo falls below WhatsApp's
 * 300 px minimum and produces no preview at all.
 */
export const getAbsoluteImageUrl = (image?: string): string => {
  return absoluteMediaUrl(image, SITE_URL) ?? `${SITE_URL}${DEFAULT_OG_IMAGE_PATH}`;
};

/** Small deterministic hash so the OG URL changes when the primary media does. */
function revisionHash(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

/**
 * Canonical social share image for a property.
 *
 * Always resolves to the normalising endpoint, which guarantees a 1200x630
 * JPEG under 600 KB (the WhatsApp contract) regardless of what the stored
 * original looks like. There is exactly one of these per listing — never one
 * per gallery photo.
 */
export function ogImageUrl(property: Property): string {
  const mediaKey = (property.image_url ?? []).filter(Boolean).join("|");
  const revision = revisionHash(
    `${property.id}|${property.og_image_url ?? ""}|${mediaKey}|${property.created_at ?? ""}`,
  );
  return `${SITE_URL}/api/og/property/${encodeURIComponent(property.id)}?v=${revision}`;
}

export function posterAndVideo(images?: string[]) {
  const image = firstImage(images);
  const video = firstVideo(images);
  return {
    posterImageUrl: absoluteMediaUrl(image, SITE_URL),
    videoUrl: absoluteMediaUrl(video, SITE_URL),
    /** Derived `-poster.jpg` sibling; may not exist for legacy videos. */
    videoPosterUrl: video ? absoluteMediaUrl(videoPosterUrl(video), SITE_URL) : undefined,
  };
}

export function formatPriceServer(
  amount?: number | string,
  currency?: string,
): string {
  const value = Number(amount || 0);
  const symbol = getCurrencySymbol(currency);
  return `${symbol}${value.toLocaleString("en-US")}`;
}

export function cleanDescription(value?: string): string {
  return (value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function titleCase(value?: string): string {
  if (!value) return "";
  const smallWords = new Set(["and", "of", "in", "the", "for", "at", "on", "to"]);
  return value
    .replace(/[-_]+/g, " ")
    .replace(/\band\b/gi, "&")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word, index) =>
      index > 0 && smallWords.has(word)
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");
}

/** "Nasarawa" -> "Nasarawa State", but never "Federal Capital Territory State". */
function stateLabel(state?: string): string {
  if (!state) return "";
  const trimmed = state.trim();
  if (/\b(state|territory)$/i.test(trimmed)) return trimmed;
  return `${trimmed} State`;
}

export function locationLabel(property: Property): string {
  const parts = [property.city, stateLabel(property.state)]
    .filter(Boolean)
    .filter((part, index, arr) => arr.indexOf(part) === index);
  return parts.join(", ");
}

export function listingTypeLabel(listingType?: string): string {
  if (!listingType) return "";
  const normalized = listingType.toLowerCase();
  if (normalized === "sale") return "for Sale";
  if (normalized === "rent") return "for Rent";
  if (normalized === "lease") return "for Lease";
  if (normalized === "shortlet") return "Shortlet";
  return `for ${titleCase(listingType)}`;
}

export function propertyTypeLabel(property: Property): string {
  const type = titleCase(property.property_type || "property");
  if (isLandProperty(property)) {
    return /land/i.test(type) ? type : `${type} Land`;
  }
  return type;
}

/**
 * Plain-language listing summary, e.g. "4 Bedroom Apartment for Rent in Lagos
 * State" or "Residential Land for Sale". Uses the affected category rules, so
 * it never emits a zero room count for land/commercial records.
 */
export function getPropertySummary(property: Property): string {
  const facts = roomSummary(property);
  const type = propertyTypeLabel(property);
  const listingType = listingTypeLabel(property.listing_type);
  const location = locationLabel(property);
  const subject = [facts, type, listingType].filter(Boolean).join(" ");
  return `${subject}${location ? ` in ${location}` : ""}`;
}

export function getPropertyTitle(property: Property): string {
  const { bedrooms } = roomCounts(property);
  const type = propertyTypeLabel(property);
  const listingType = listingTypeLabel(property.listing_type);
  const location = locationLabel(property);
  const price = property.price || property.total_package;
  const priceText = price
    ? ` - ${formatPriceServer(price, property.currency)}`
    : "";

  const subject = [
    bedrooms ? `${bedrooms} Bedroom` : "",
    type,
    listingType,
  ]
    .filter(Boolean)
    .join(" ");

  const locationText = location ? ` in ${location}` : "";
  return `${subject}${locationText}${priceText}`;
}

// A plain-language summary ("4 bedroom apartment for rent in Lagos State")
// that mirrors how people actually phrase these searches, prepended to the
// realtor's own listing text so both the typed-out facts and free-form
// wording are present for matching.
export function getPropertySeoDescription(property: Property): string {
  const land = isLandProperty(property);
  const facts = land
    ? [roomSummary(property), landSizeLabel(property)].filter(Boolean).join(", ")
    : roomSummary(property);
  const type = propertyTypeLabel(property).toLowerCase();
  const listingType = listingTypeLabel(property.listing_type);
  const location = locationLabel(property);
  const structuredSummary = [
    facts,
    type,
    listingType,
    location ? `in ${location}` : "",
  ]
    .filter(Boolean)
    .join(" ");
  const price = property.price || property.total_package;
  const priceText = price
    ? formatPriceServer(price, property.currency)
    : "";
  const description = cleanDescription(property.description);
  const withPrice = [structuredSummary, description, priceText]
    .filter(Boolean)
    .join(" | ");
  if (withPrice.length <= 160) return withPrice;

  // Keep the price intact and trim the realtor's free text instead: slicing the
  // joined string used to cut it mid-number ("| ₦17").
  if (priceText) {
    const room = 160 - structuredSummary.length - priceText.length - 6;
    if (room > 40) {
      const trimmed = `${description.slice(0, room - 1).trimEnd()}…`;
      return [structuredSummary, trimmed, priceText].join(" | ");
    }
  }

  const base = [structuredSummary, description].filter(Boolean).join(" | ");
  if (base.length <= 160) return base;
  return `${base.slice(0, 157).trimEnd()}…`;
}

export function propertyCanonicalPath(property: Property): string {
  return buildPropertyDetailPath(property);
}

export function propertyStatePath(property: Property): string | null {
  const stateSlug = propertyStateSlug(property);
  return stateSlug ? buildLocationPath(stateSlug) : null;
}

export function buildPropertyMetadata(property: Property): Metadata {
  const { videoUrl } = posterAndVideo(property.image_url);
  const seoDescription = getPropertySeoDescription(property);
  const title = getPropertyTitle(property);
  const canonicalPath = propertyCanonicalPath(property);
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;
  const image = ogImageUrl(property);

  return {
    title,
    description: seoDescription,
    keywords: getPropertyKeywords(property),
    alternates: { canonical: canonicalPath },
    robots: { index: isPropertyIndexable(property), follow: true },
    openGraph: {
      title,
      description: seoDescription,
      type: "website",
      url: canonicalUrl,
      siteName: "Find Nigeria Properties",
      locale: "en_NG",
      // Dimensions are hard-coded *and true*: /api/og/property always emits
      // exactly this size. Do not "fix" this to the source image's size.
      images: [
        {
          url: image,
          secureUrl: image,
          type: "image/jpeg",
          width: OG_IMAGE_WIDTH,
          height: OG_IMAGE_HEIGHT,
          alt: title,
        },
      ],
      ...(videoUrl
        ? { videos: [{ url: videoUrl, type: "video/mp4", secureUrl: videoUrl }] }
        : {}),
    },
    twitter: {
      card: "summary_large_image",
      title,
      description: seoDescription,
      images: [
        {
          url: image,
          secureUrl: image,
          type: "image/jpeg",
          width: OG_IMAGE_WIDTH,
          height: OG_IMAGE_HEIGHT,
          alt: title,
        },
      ],
    },
  };
}

export function getPropertyKeywords(property: Property): string {
  const { bedrooms } = roomCounts(property);
  const typeLabel = (property.property_type || "property").toLowerCase();
  const bedroomPhrase = bedrooms ? `${bedrooms} bedroom ${typeLabel}` : undefined;
  return [
    bedroomPhrase,
    property.property_type,
    property.listing_type,
    property.city,
    property.state,
    property.state ? stateLabel(property.state) : undefined,
    property.city && property.state
      ? `properties in ${property.city}, ${property.state}`
      : undefined,
    locationLabel(property)
      ? `${isLandProperty(property) ? "land" : "property"} for ${property.listing_type ?? "sale"} in ${locationLabel(property)}`
      : undefined,
    "Nigeria property",
    "real estate Nigeria",
  ]
    .filter(Boolean)
    .join(", ");
}

export function isPropertyIndexable(property: Property): boolean {
  const availability = property.availability_status?.toLowerCase();
  const legacyStatus = property.status?.toLowerCase();
  return (
    Boolean((property.description || property.address) && property.state) &&
    property.approval_status?.toLowerCase() !== "rejected" &&
    property.approval_status?.toLowerCase() !== "pending" &&
    (!availability || availability === "available") &&
    ![
      "pending",
      "rejected",
      "under-review",
      "under-contract",
      "sold",
      "rented",
      "leased",
      "inactive",
    ].includes(
      legacyStatus || "",
    )
  );
}

export function offerAvailability(property: Property): string | undefined {
  const status = (
    property.availability_status ||
    property.status ||
    ""
  ).toLowerCase();
  if (status === "available") return "https://schema.org/InStock";
  if (status === "under-contract") return "https://schema.org/LimitedAvailability";
  if (["sold", "rented", "leased"].includes(status)) {
    return "https://schema.org/OutOfStock";
  }
  return undefined;
}

export async function fetchProperty(id: string): Promise<Property | null> {
  try {
    const response = await axiosWithoutToken.get(`/properties/${id}`);
    return response?.data?.data || null;
  } catch (error) {
    console.error(`[property-seo] Failed to fetch property ${id}:`, error);
    return null;
  }
}
